import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Verfügbare Schriftgrößen
const FONT_SIZES = ["10", "11", "12", "13", "14", "15", "16", "17", "18", "20", "22", "24", "26", "28", "30", "32"];

// Verfügbare Schriftgewichte
const FONT_WEIGHTS = [
  { value: "300", label: "Light (300)" },
  { value: "400", label: "Regular (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "SemiBold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "800", label: "ExtraBold (800)" },
];

// Standard Google Fonts
const DEFAULT_FONTS = [
  "Titillium Web",
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Source Sans Pro",
  "Nunito",
  "Poppins",
  "Raleway",
  "Ubuntu",
  "Merriweather",
  "Playfair Display",
  "PT Sans",
  "Work Sans",
];

// Einstellungs-Kategorien mit Beschreibungen
const SETTING_CATEGORIES = [
  { key: "navigation", label: "Navigation", description: "Tabs in der Hauptnavigation" },
  { key: "h1", label: "Überschrift H1", description: "Große Seitenüberschriften" },
  { key: "h2", label: "Überschrift H2", description: "Abschnittsüberschriften (z.B. 'Kinder pro Gruppe')" },
  { key: "h3", label: "Überschrift H3", description: "Unterüberschriften in Karten" },
  { key: "body", label: "Fließtext", description: "Normaler Text, Beschreibungen" },
  { key: "names", label: "Namen", description: "Kindernamen in Listen und Anwesenheit" },
  { key: "badges", label: "Badges", description: "Gruppen-Badges, Status-Labels" },
  { key: "labels", label: "Labels", description: "Formular-Beschriftungen" },
  { key: "small", label: "Kleintext", description: "Hinweistexte, Fußnoten" },
];

export function FontSettingsManager({ authToken }) {
  const [settings, setSettings] = useState(null);
  const [availableFonts, setAvailableFonts] = useState(DEFAULT_FONTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  // Einstellungen laden
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const [settingsRes, fontsRes] = await Promise.all([
          axios.get(`${API_BASE}/api/font-settings`, authHeaders()),
          axios.get(`${API_BASE}/api/font-settings/available-fonts`, authHeaders()),
        ]);
        setSettings(settingsRes.data);
        if (fontsRes.data?.fonts) {
          setAvailableFonts(fontsRes.data.fonts);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Schrifteinstellungen:", error);
        setMessage({ type: "error", text: "Fehler beim Laden der Einstellungen" });
      }
      setLoading(false);
    };
    loadSettings();
  }, [authHeaders]);

  // Einstellung ändern
  const handleChange = (category, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: field ? { ...prev[category], [field]: value } : value,
    }));
    setHasChanges(true);
    setMessage(null);
  };

  // Speichern
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/font-settings`, settings, authHeaders());
      setMessage({ type: "success", text: "Einstellungen gespeichert! Die Seite wird neu geladen..." });
      setHasChanges(false);
      // Seite neu laden, damit CSS-Variablen aktualisiert werden
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      setMessage({ type: "error", text: "Fehler beim Speichern der Einstellungen" });
    }
    setSaving(false);
  };

  // Zurücksetzen auf Standard
  const handleReset = async () => {
    if (!window.confirm("Möchten Sie alle Schrifteinstellungen auf die Standardwerte zurücksetzen?")) {
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/api/font-settings/reset`, {}, authHeaders());
      setSettings(res.data);
      setMessage({ type: "success", text: "Einstellungen zurückgesetzt! Die Seite wird neu geladen..." });
      setHasChanges(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Fehler beim Zurücksetzen:", error);
      setMessage({ type: "error", text: "Fehler beim Zurücksetzen der Einstellungen" });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="rounded-lg bg-rose-50 p-4 text-center text-rose-700">
        Einstellungen konnten nicht geladen werden.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="font-settings-manager">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-violet-50 to-white p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Schriftarten & Größen</h3>
            <p className="mt-1 text-sm text-slate-500">
              Passen Sie die Schriftarten und -größen der gesamten Anwendung an.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Standard
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </div>

        {/* Status-Meldung */}
        {message && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${
              message.type === "success"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Schriftart-Auswahl */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-4 text-base font-semibold text-slate-900">Schriftarten (Google Fonts)</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Hauptschrift (Body, Navigation)</label>
            <select
              value={settings.primaryFont || "Titillium Web"}
              onChange={(e) => handleChange("primaryFont", null, e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-violet-500 focus:outline-none"
            >
              {availableFonts.map((font) => (
                <option key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Überschriften-Schrift</label>
            <select
              value={settings.headingFont || "Titillium Web"}
              onChange={(e) => handleChange("headingFont", null, e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-violet-500 focus:outline-none"
            >
              {availableFonts.map((font) => (
                <option key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Tipp: Verschiedene Schriften für Überschriften und Fließtext können einen schönen Kontrast erzeugen.
        </p>
      </div>

      {/* Schriftgrößen & Gewichte */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-4 text-base font-semibold text-slate-900">Schriftgrößen & Gewichte</h4>
        <div className="space-y-4">
          {SETTING_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              className="grid items-center gap-3 rounded-lg bg-slate-50 p-3 sm:grid-cols-[200px_1fr_1fr]"
            >
              <div>
                <div className="text-sm font-semibold text-slate-800">{cat.label}</div>
                <div className="text-xs text-slate-500">{cat.description}</div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Größe (px)</label>
                <select
                  value={settings[cat.key]?.size || "14"}
                  onChange={(e) => handleChange(cat.key, "size", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
                >
                  {FONT_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}px
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Gewicht</label>
                <select
                  value={settings[cat.key]?.weight || "500"}
                  onChange={(e) => handleChange(cat.key, "weight", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
                >
                  {FONT_WEIGHTS.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live-Vorschau */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-4 text-base font-semibold text-slate-900">Live-Vorschau</h4>
        <div
          className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
          style={{ fontFamily: settings.primaryFont || "Titillium Web" }}
        >
          <div
            style={{
              fontFamily: settings.headingFont || settings.primaryFont,
              fontSize: `${settings.h1?.size || 24}px`,
              fontWeight: settings.h1?.weight || 700,
            }}
          >
            Überschrift H1 - Schwimm-Admin
          </div>
          <div
            style={{
              fontFamily: settings.headingFont || settings.primaryFont,
              fontSize: `${settings.h2?.size || 18}px`,
              fontWeight: settings.h2?.weight || 600,
            }}
          >
            Überschrift H2 - Kinder pro Gruppe
          </div>
          <div
            style={{
              fontFamily: settings.headingFont || settings.primaryFont,
              fontSize: `${settings.h3?.size || 16}px`,
              fontWeight: settings.h3?.weight || 600,
            }}
          >
            Überschrift H3 - Anwesenheit erfassen
          </div>
          <p
            style={{
              fontSize: `${settings.body?.size || 14}px`,
              fontWeight: settings.body?.weight || 500,
            }}
          >
            Dies ist ein Beispiel für normalen Fließtext. So wird der Großteil der Anwendung dargestellt.
          </p>
          <div className="flex items-center gap-3 rounded-lg bg-white p-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-200 text-violet-700 font-semibold">AM</div>
            <span
              style={{
                fontSize: `${settings.names?.size || 16}px`,
                fontWeight: settings.names?.weight || 600,
              }}
              className="text-slate-900"
            >
              Aaron Mustermann
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className="rounded-full bg-violet-100 px-3 py-1 text-violet-700"
              style={{
                fontSize: `${settings.badges?.size || 14}px`,
                fontWeight: settings.badges?.weight || 600,
              }}
            >
              Gruppe 1 · 16:30
            </span>
            <span
              className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700"
              style={{
                fontSize: `${settings.badges?.size || 14}px`,
                fontWeight: settings.badges?.weight || 600,
              }}
            >
              Seepferdchen
            </span>
            <span
              className="rounded-full bg-amber-100 px-3 py-1 text-amber-700"
              style={{
                fontSize: `${settings.badges?.size || 14}px`,
                fontWeight: settings.badges?.weight || 600,
              }}
            >
              Archiv (5)
            </span>
          </div>
          <div className="flex gap-4">
            <button
              className="rounded-lg bg-slate-900 px-4 py-2 text-white"
              style={{
                fontSize: `${settings.navigation?.size || 16}px`,
                fontWeight: settings.navigation?.weight || 700,
              }}
            >
              Kinder
            </button>
            <button
              className="rounded-lg bg-slate-100 px-4 py-2 text-slate-700"
              style={{
                fontSize: `${settings.navigation?.size || 16}px`,
                fontWeight: settings.navigation?.weight || 700,
              }}
            >
              Anwesenheit
            </button>
          </div>
          <p
            className="text-slate-400"
            style={{
              fontSize: `${settings.small?.size || 12}px`,
              fontWeight: settings.small?.weight || 500,
            }}
          >
            Kleintext: Hinweise, Fußnoten und technische Informationen
          </p>
        </div>
      </div>

      {/* Hinweis */}
      <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
        <strong>Hinweis:</strong> Nach dem Speichern wird die Seite automatisch neu geladen, damit alle Änderungen wirksam werden.
      </div>
    </div>
  );
}
